import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Building2, Globe, ChevronLeft, Star, Award, Tent, MapPin } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_SIZES = { large: 'w-24 h-24', medium: 'w-16 h-16', small: 'w-12 h-12' };

const VendorDirectoryPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('vendors');

  useEffect(() => {
    const fetch = async () => {
      try {
        const [eventRes, vendorRes, sponsorRes] = await Promise.all([
          axios.get(`${API_URL}/activities/event/${eventId}`),
          axios.get(`${API_URL}/events/${eventId}/vendors`),
          axios.get(`${API_URL}/events/${eventId}/sponsors`)
        ]);
        setEvent(eventRes.data);
        setVendors(vendorRes.data);
        setSponsors(sponsorRes.data);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [eventId]);

  if (loading) return (
    <div className="min-h-screen bg-slate-900">
      <Navbar variant="dark" />
      <div className="max-w-6xl mx-auto px-4 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array(6).fill(0).map((_, i) => <div key={i} className="h-48 bg-white/5 rounded-lg animate-pulse" />)}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar variant="dark" />
      <div className="max-w-6xl mx-auto px-4 py-12">
        <Link to={`/events/${eventId}`}>
          <Button variant="ghost" className="text-white/60 hover:text-white mb-6">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Event
          </Button>
        </Link>

        <div className="text-center mb-10">
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="vendor-directory-title">
            {event?.title}
          </h1>
          <p className="text-white/60">Vendors & Sponsors Directory</p>
          {event?.location && (
            <p className="text-white/40 text-sm mt-1 flex items-center justify-center gap-1">
              <MapPin className="w-3 h-3" /> {event.location}
            </p>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center gap-2 mb-8">
          <Button
            variant={tab === 'vendors' ? 'default' : 'outline'}
            onClick={() => setTab('vendors')}
            className={tab === 'vendors' ? 'bg-cameroon-green' : 'border-white/20 text-white hover:bg-white/10'}
            data-testid="tab-vendors"
          >
            <Tent className="w-4 h-4 mr-2" /> Vendors ({vendors.length})
          </Button>
          <Button
            variant={tab === 'sponsors' ? 'default' : 'outline'}
            onClick={() => setTab('sponsors')}
            className={tab === 'sponsors' ? 'bg-cameroon-yellow text-slate-900' : 'border-white/20 text-white hover:bg-white/10'}
            data-testid="tab-sponsors"
          >
            <Award className="w-4 h-4 mr-2" /> Sponsors ({sponsors.length})
          </Button>
        </div>

        {/* Sponsors Section */}
        {tab === 'sponsors' && (
          <div className="space-y-8" data-testid="sponsors-section">
            {sponsors.length === 0 ? (
              <div className="text-center py-16">
                <Award className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/40">No sponsors yet. Be the first to sponsor this event!</p>
                <Link to={`/events/${eventId}/sponsor`}>
                  <Button className="mt-4 bg-cameroon-yellow text-slate-900">Become a Sponsor</Button>
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {sponsors.map((s) => (
                  <Card key={s.id} className="glass border-white/10 hover:border-white/20 transition-all overflow-hidden group" data-testid={`sponsor-card-${s.id}`}>
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-center gap-3">
                        {s.logo_url ? (
                          <img src={s.logo_url} alt={s.company_name} className={`${LOGO_SIZES[s.logo_size] || LOGO_SIZES.medium} rounded-lg object-contain bg-white/10 p-1`} />
                        ) : (
                          <div className={`${LOGO_SIZES[s.logo_size] || LOGO_SIZES.medium} rounded-lg bg-cameroon-yellow/20 flex items-center justify-center`}>
                            <Building2 className="w-6 h-6 text-cameroon-yellow" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-white">{s.company_name}</h3>
                          <Badge className="bg-cameroon-yellow/20 text-cameroon-yellow border-0 text-xs">{s.package_name}</Badge>
                        </div>
                      </div>
                      {s.company_description && <p className="text-sm text-white/60 line-clamp-2">{s.company_description}</p>}
                      {s.website_url && (
                        <a href={s.website_url} target="_blank" rel="noopener noreferrer" className="text-sm text-cameroon-green hover:underline flex items-center gap-1">
                          <Globe className="w-3 h-3" /> Visit Website
                        </a>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Vendors Section */}
        {tab === 'vendors' && (
          <div data-testid="vendors-section">
            {vendors.length === 0 ? (
              <div className="text-center py-16">
                <Tent className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/40">No vendors yet. Reserve your tent!</p>
                <Link to={`/events/${eventId}/reserve-tent`}>
                  <Button className="mt-4 bg-cameroon-green">Reserve a Tent</Button>
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {vendors.map((v) => (
                  <Card key={v.id} className="glass border-white/10 hover:border-white/20 transition-all" data-testid={`vendor-card-${v.id}`}>
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-center gap-3">
                        {v.logo_url ? (
                          <img src={v.logo_url} alt={v.business_name} className="w-12 h-12 rounded-lg object-contain bg-white/10 p-1" />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-cameroon-green/20 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-cameroon-green" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-white">{v.business_name}</h3>
                          <Badge variant="outline" className="border-white/10 text-white/60 text-xs capitalize">{v.business_type?.replace('_', ' ')}</Badge>
                        </div>
                      </div>
                      {v.business_description && <p className="text-sm text-white/60 line-clamp-2">{v.business_description}</p>}
                      <div className="flex items-center gap-2 text-xs text-white/40">
                        <Tent className="w-3 h-3" />
                        <span>{v.tent_type_name}</span>
                      </div>
                      {v.website_url && (
                        <a href={v.website_url} target="_blank" rel="noopener noreferrer" className="text-sm text-cameroon-green hover:underline flex items-center gap-1">
                          <Globe className="w-3 h-3" /> Visit Website
                        </a>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorDirectoryPage;
